/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue, no-promise-executor-return, consistent-return, no-void, max-len, class-methods-use-this, no-underscore-dangle, no-undef, no-plusplus, prefer-destructuring, no-param-reassign, no-use-before-define, no-nested-ternary, max-classes-per-file, no-unused-vars */
// Ported from newdtrader (packages/core/src/_common/base/dfunded); kept in its
// original style so fixes can be carried across the two copies.
/**
 * v2-websocket-wrapper.js
 *
 * Wraps a native WebSocket so that:
 *  - Outgoing messages get v2 protocol transformation (e.g. symbol → underlying_symbol).
 *  - Incoming messages get normalized back to the v1 shape that legacy callers expect
 *    (e.g. underlying_symbol → symbol).
 *  - `{ authorize: <token> }` requests are suppressed and a synthetic authorize
 *    response is dispatched, so callers waiting on `expectResponse('authorize')`
 *    (DerivAPIBasic.authorized.X(), the trader store, etc.) still resolve.
 *
 * Used by socket_base.js when the app is running in Deriv v2 mode and the
 * WebSocket URL is the OTP-pre-authenticated v2 endpoint.
 *
 * The transform/normalize logic is duplicated from packages/api-v2/src/v2/* so this
 * file has no cross-package dependency on @deriv/api-v2 (avoids circular imports).
 */

// Per-message logging is verbose (every request, every response, every stub).
// Gate it behind a localStorage flag so production consoles stay clean. Enable in
// the browser console with: localStorage.setItem('bridge_debug', '1').
const isDebug = () => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem('bridge_debug') === '1'; } catch (_e) { return false; }
};
const debug = (...args) => { if (isDebug()) console.log(...args); }; // eslint-disable-line no-console

const STRIP_KEYS = new Set(['loginid', 'product_type']);
// Explicit public-only surface. Never send credentials, buys, sells, balances,
// account queries or future unknown commands through the guest connection.
const PUBLIC_REQUESTS = new Set([
    'active_symbols', 'ticks', 'ticks_history', 'contracts_for', 'trading_times',
    'ping', 'time', 'forget', 'forget_all', 'website_status', 'payout_currencies', 'landing_company',
]);
const PUBLIC_PARAMETERS = new Set([
    'subscribe', 'req_id', 'passthrough', 'symbol', 'underlying_symbol', 'product_type',
    'barrier_category', 'currency', 'landing_company', 'landing_company_shortcode',
    'adjust_start_time', 'count', 'end', 'start', 'style', 'granularity',
]);

// Response types whose body we dump in full when debug logging is on — useful
// while debugging which v2 → v1 field mappings are missing. Add/remove as needed.
const VERBOSE_RESPONSE_TYPES = new Set([
    'buy',
    'sell',
    'proposal',
    'proposal_open_contract',
    'transaction',
    'portfolio',
    'open_contract',
    'contract',
]);

function transformV2Request(req) {
    if (!req || typeof req !== 'object') return req;

    // authorize is suppressed entirely — OTP URL is pre-authenticated
    if ('authorize' in req && typeof req.authorize === 'string') {
        return null;
    }

    const isBalance = 'balance' in req;

    const out = {};
    for (const [key, value] of Object.entries(req)) {
        if (STRIP_KEYS.has(key)) continue;
        if (key === 'symbol') {
            out.underlying_symbol = value;
            continue;
        }
        if (isBalance && (key === 'account' || (key === 'accounts' && value === 'all'))) {
            // v2 `balance` returns the authorized account only — `account` and
            // `accounts: 'all'` parameters are rejected as InputValidationFailed.
            continue;
        }
        out[key] = value;
    }
    return out;
}

// Field aliases: when one of these v2 keys is found, ALSO populate the v1 name
// alongside the v2 name. We keep both because some legacy code paths (e.g.
// purchase.tsx checking `contract_info.underlying_symbol || contract_info.underlying`)
// look for the original field, while other code uses the v2 name directly. Renaming
// without keeping the original silently breaks those checks.
const FIELD_ALIASES = {
    underlying_symbol     : ['symbol'],
    underlying_symbol_name: ['display_name'],
    underlying_symbol_type: ['symbol_type'],
    exit_spot             : ['sell_spot'],
};

const symbolLabel = value => ({
    synthetic_index: 'Derived', synthetics     : 'Derived', random_index   : 'Continuous Indices',
    random_daily   : 'Daily Reset Indices', major_pairs    : 'Major Pairs',
}[value] || String(value || '').split('_').filter(Boolean).map(word => word[0].toUpperCase() + word.slice(1)).join(' '));

function normalizeV2Response(input) {
    if (Array.isArray(input)) return input.map(normalizeV2Response);
    if (input === null || typeof input !== 'object') return input;
    const out = {};
    for (const [key, value] of Object.entries(input)) {
        const normalizedValue = normalizeV2Response(value);
        out[key] = normalizedValue;
        const aliases = FIELD_ALIASES[key];
        if (aliases) {
            for (const aliasKey of aliases) {
                if (out[aliasKey] === undefined) out[aliasKey] = normalizedValue;
            }
        }
    }

    // Verified public active_symbols (2026-09-08) includes market/subgroup/
    // submarket but omits their display names and legacy pip. SmartCharts
    // sorts submarket_display_name and calls pip.toString() without fallbacks.
    // Some public Crash/Boom and Range Break records have symbol_type: ''.
    if (out.symbol && out.market && out.pip_size !== undefined) {
        if (out.pip === undefined) out.pip = Number(out.pip_size);
        if (!out.market_display_name) out.market_display_name = symbolLabel(out.market || out.symbol_type);
        if (!out.subgroup_display_name) {
            out.subgroup_display_name = symbolLabel(out.subgroup || out.market || out.symbol_type);
        }
        if (!out.submarket_display_name) {
            out.submarket_display_name = symbolLabel(out.submarket || out.market || out.symbol_type);
        }
    }

    // Synthesize v1-compatible display_value aliases for contract-shaped payloads
    // (proposal_open_contract, buy responses with contract data, etc.). The legacy
    // accumulator UI, digit chart, and contract-trade-store read these fields by name
    // and silently fall back to undefined if absent — which is why the Buy button
    // doesn't switch to Sell, profit doesn't tick, and digit balls don't animate.
    const isContractShape = 'entry_spot' in out || 'current_spot' in out;
    if (isContractShape) {
        if (out.entry_spot !== undefined && out.entry_tick === undefined) {
            out.entry_tick = out.entry_spot;
        }
        if (out.entry_spot !== undefined && out.entry_tick_display_value === undefined) {
            out.entry_tick_display_value = String(out.entry_spot);
        }
        if (out.entry_spot !== undefined && out.entry_spot_display_value === undefined) {
            out.entry_spot_display_value = String(out.entry_spot);
        }
        if (out.entry_spot_time !== undefined && out.entry_tick_time === undefined) {
            out.entry_tick_time = out.entry_spot_time;
        }
        if (out.current_spot !== undefined && out.current_spot_display_value === undefined) {
            out.current_spot_display_value = String(out.current_spot);
        }
        // Also keep `underlying` alongside `underlying_symbol`/`symbol` for the
        // very oldest code paths that look for that field name.
        if (out.symbol !== undefined && out.underlying === undefined) {
            out.underlying = out.symbol;
        }
    }

    return out;
}

/**
 * Methods the v2 trading endpoint does NOT support but legacy code paths
 * (trade-store, client-store, app init) still wait on. We synthesize a
 * minimally-valid response locally so `expectResponse(name)` / `WS.wait(name)`
 * resolves and the UI can proceed past its load gate.
 *
 * Each value is a function `(req) => responsePayload` that returns the body
 * placed under the request's name in the response (e.g. for `website_status`,
 * the function returns the value of `website_status` in the reply object).
 */
const V1_ONLY_STUBS = {
    website_status: () => ({
        site_status    : 'up',
        message        : '',
        api_call_limits: {
            max_proposal_subscription: { applies_to: 'subscribing to proposal', max: 5 },
            max_requestes_general    : { applies_to: 'general requests', hourly: 14400, minutely: 60 },
            max_requests_outcome     : { applies_to: 'requests requiring authentication', hourly: 14400, minutely: 60 },
            max_requests_pricing     : { applies_to: 'requests using pricing parameters', hourly: 1500, minutely: 60 },
        },
        currencies_config       : {},
        terms_conditions_version: '1.0.0',
        clients_country         : 'us',
    }),
    payout_currencies: () => {
        try {
            const accounts = JSON.parse(sessionStorage.getItem('v2_accounts') || '[]');
            const currencies = Array.from(new Set(accounts.map(a => a.currency).filter(Boolean)));
            return currencies.length ? currencies : ['USD'];
        } catch (_e) {
            return ['USD'];
        }
    },
    get_settings: () => {
        try {
            const profile = JSON.parse(sessionStorage.getItem('v2_user_profile') || '{}');
            return {
                email       : profile.email || '',
                country     : profile.country || profile.residence || '',
                country_code: profile.country_code || '',
                first_name  : profile.first_name || '',
                last_name   : profile.last_name || '',
                residence   : profile.residence || profile.country || '',
                user_hash   : '',
            };
        } catch (_e) {
            return {};
        }
    },
    get_account_status: () => ({
        status                       : [],
        risk_classification          : 'low',
        prompt_client_to_authenticate: 0,
        currency_config              : {},
    }),
    landing_company    : () => ({}),
    mt5_login_list     : () => [],
    trading_servers    : () => [],
    cashier_payments   : () => ({ crypto: [] }),
    p2p_advertiser_info: () => ({ is_listed: 0 }),
    // sell_expired is a v1 cleanup request — sells any expired contracts. v2
    // expires them server-side automatically, so stub a no-op count of 0.
    sell_expired       : () => ({ count: 0 }),
};

/**
 * Names where the response body lives directly under the request key
 * (e.g. response = { website_status: {...}, msg_type: 'website_status' }).
 * Some v1 responses use a different shape — extend here if needed.
 */
const buildStubResponse = (requestType, parsed) => {
    const stub = V1_ONLY_STUBS[requestType];
    if (!stub) return null;
    return {
        msg_type     : requestType,
        echo_req     : parsed,
        req_id       : parsed.req_id,
        [requestType]: stub(parsed),
    };
};

/**
 * A WebSocket-compatible class that wraps a real WebSocket and applies v2
 * request/response transformation. Conforms to the WebSocket interface so
 * DerivAPIBasic and any other consumer can use it transparently.
 *
 * @param {string} url - The v2 OTP WebSocket URL.
 * @param {object} authorizeStub - The synthetic `authorize` response payload to
 *   dispatch when an authorize request is intercepted. Should look roughly like
 *   `{ loginid, currency, email, fullname, is_virtual, account_list, ... }`.
 */
class V2WrappedWebSocket extends EventTarget {
    constructor(url, authorizeStub = {}, { publicOnly = false } = {}) {
        super();
        this._authorizeStub = authorizeStub;
        this._publicOnly = publicOnly;
        this._real = new WebSocket(url);
        this.onopen = null;
        this.onmessage = null;
        this.onclose = null;
        this.onerror = null;

        this._real.addEventListener('open', e => {
            const event = new Event('open');
            this.dispatchEvent(event);
            if (typeof this.onopen === 'function') this.onopen(event);
        });

        this._real.addEventListener('message', event => {
            let normalizedData = event.data;
            try {
                const parsed = JSON.parse(event.data);
                const normalized = normalizeV2Response(parsed);
                normalizedData = JSON.stringify(normalized);
                if (isDebug()) {
                    const msgType = normalized.msg_type || '(no msg_type)';
                    if (msgType === 'tick') {
                        if (!this._tickLogCount) this._tickLogCount = 0;
                        if (this._tickLogCount < 2) {
                            debug('[V2WS] tick FULL:', normalized);
                            this._tickLogCount++;
                        }
                    } else if (VERBOSE_RESPONSE_TYPES.has(msgType)) {
                        debug('[V2WS] response FULL:', msgType, normalized);
                    } else {
                        debug('[V2WS] response in:', msgType, normalized.error ? `ERROR: ${normalized.error.code} ${normalized.error.message}` : 'ok', { req_id: normalized.req_id, keys: Object.keys(normalized) });
                    }
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[V2WS] non-JSON message:', event.data);
            }
            const newEvent = new MessageEvent('message', { data: normalizedData, origin: event.origin });
            this.dispatchEvent(newEvent);
            if (typeof this.onmessage === 'function') this.onmessage(newEvent);
        });

        this._real.addEventListener('close', e => {
            const event = new CloseEvent('close', { code: e.code, reason: e.reason, wasClean: e.wasClean });
            this.dispatchEvent(event);
            if (typeof this.onclose === 'function') this.onclose(event);
        });

        this._real.addEventListener('error', () => {
            const event = new Event('error');
            this.dispatchEvent(event);
            if (typeof this.onerror === 'function') this.onerror(event);
        });
    }

    get readyState() { return this._real.readyState; }
    get url() { return this._real.url; }
    get protocol() { return this._real.protocol; }
    get bufferedAmount() { return this._real.bufferedAmount; }
    get extensions() { return this._real.extensions; }
    get binaryType() { return this._real.binaryType; }
    set binaryType(v) { this._real.binaryType = v; }

    get CONNECTING() { return WebSocket.CONNECTING; }
    get OPEN() { return WebSocket.OPEN; }
    get CLOSING() { return WebSocket.CLOSING; }
    get CLOSED() { return WebSocket.CLOSED; }

    send(data) {
        if (typeof data !== 'string') {
            if (this._publicOnly) throw new Error('Public market requests must be JSON.');
            return this._real.send(data);
        }
        let parsed;
        try {
            parsed = JSON.parse(data);
        } catch (e) {
            if (this._publicOnly) throw new Error('Public market requests must be valid JSON.');
            return this._real.send(data);
        }
        if (this._publicOnly) {
            const keys = Object.keys(parsed).filter(k => !['subscribe', 'req_id', 'passthrough'].includes(k));
            const command = keys.find(k => PUBLIC_REQUESTS.has(k));
            // Allow only parameters belonging to a recognized market command;
            // checking every financial key also rejects a mixed {ticks,buy} payload.
            if (!command || keys.some(k => k !== command && !PUBLIC_PARAMETERS.has(k))) {
                this._dispatchSynthetic({ msg_type: keys[0], req_id  : parsed.req_id,
                    error   : { code: 'AuthorizationRequired', message: 'Log in to access trading and account actions.' } });
                return;
            }
        }
        const transformed = transformV2Request(parsed);
        if (transformed === null) {
            // authorize suppressed — dispatch a synthetic authorize response so
            // expectResponse('authorize') / WS.authorized.X() can resolve.
            if (parsed.authorize !== undefined) {
                debug('[V2WS] suppressing authorize, dispatching stub');
                this._dispatchSynthetic({
                    msg_type : 'authorize',
                    echo_req : parsed,
                    req_id   : parsed.req_id,
                    authorize: this._authorizeStub,
                });
            }
            return;
        }

        // Detect v1-only methods the v2 endpoint doesn't support and stub them
        // locally so trade-store waiters (WS.wait('website_status'), etc.) resolve.
        const requestType = Object.keys(parsed).find(
            k => k !== 'subscribe' && k !== 'req_id' && k !== 'passthrough'
        );
        const stub = buildStubResponse(requestType, parsed);
        if (stub) {
            debug('[V2WS] stubbing v1-only request:', requestType);
            this._dispatchSynthetic(stub);
            return;
        }

        if (transformed !== parsed) debug('[V2WS] transformed request:', Object.keys(parsed)[0], '→', Object.keys(transformed));
        return this._real.send(JSON.stringify(transformed));
    }

    _dispatchSynthetic(payload) {
        setTimeout(() => {
            const event = new MessageEvent('message', { data: JSON.stringify(payload) });
            this.dispatchEvent(event);
            if (typeof this.onmessage === 'function') this.onmessage(event);
        }, 0);
    }

    close(code, reason) {
        return this._real.close(code, reason);
    }
}

module.exports = {
    V2WrappedWebSocket,
    transformV2Request,
    normalizeV2Response,
    V1_ONLY_STUBS,
    buildStubResponse,
};
